import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../utils/api';

interface Organization {
  organization_id: string;
  name: string;
  organization_code: string;
}

// Full user interface from the API
interface ApiUser {
  user_id: string;
  username: string;
  email: string;
  organization_id: string;
  role: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// User data for form submissions (subset of fields needed for create/update)
interface UserFormData {
  user_id?: string;
  username: string;
  email: string;
  password?: string;
  organization_id: string;
  role: string;
  is_active?: boolean;
}

interface UserFormProps {
  user?: ApiUser;
  onSubmit: (userData: UserFormData) => Promise<void>;
  onCancel: () => void;
}

const UserForm: React.FC<UserFormProps> = ({ user, onSubmit, onCancel }) => {
  const [userData, setUserData] = useState<UserFormData>({
    username: '',
    email: '',
    password: '',
    organization_id: '',
    role: 'user',
  });
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const isEditMode = !!user?.user_id;
  
  // Load organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const data = await fetchWithAuth('/v1/auth/admin/organizations');
        setOrganizations(data);
        
        // If this is a new user and we have organizations, set the first one as default
        if (!isEditMode && data.length > 0 && !userData.organization_id) {
          setUserData(prev => ({
            ...prev,
            organization_id: data[0].organization_id
          }));
        }
      } catch (err) {
        setError('Failed to load organizations');
        console.error(err);
      }
    };
    
    fetchOrganizations();
  }, []);
  
  // If editing, load user data
  useEffect(() => {
    if (user) {
      setUserData({
        user_id: user.user_id,
        username: user.username,
        email: user.email,
        organization_id: user.organization_id,
        role: user.role,
        is_active: user.is_active !== false
      });
    }
  }, [user]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setUserData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setUserData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await onSubmit(userData);
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 p-4 rounded-lg text-red-800 mb-4">
          {error}
        </div>
      )}
      
      <div>
        <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
          Username *
        </label>
        <input
          type="text"
          id="username"
          name="username"
          value={userData.username}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary"
          required
        />
      </div>
      
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={userData.email}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary"
          required
        />
      </div>
      
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
          Password {!isEditMode && '*'}
        </label>
        <input
          type="password"
          id="password"
          name="password"
          value={userData.password || ''}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary"
          required={!isEditMode}
          placeholder={isEditMode ? "Leave blank to keep current password" : ""}
        />
      </div>
      
      <div>
        <label htmlFor="organization_id" className="block text-sm font-medium text-gray-700 mb-1">
          Organization {isEditMode ? '(Cannot be changed)' : '*'}
        </label>
        <select
          id="organization_id"
          name="organization_id"
          value={userData.organization_id}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary"
          required
          disabled={isEditMode}
        >
          <option value="">Select an organization</option>
          {organizations.map(org => (
            <option key={org.organization_id} value={org.organization_id}>
              {org.name} ({org.organization_code})
            </option>
          ))}
        </select>
        {isEditMode && (
          <p className="mt-1 text-sm text-gray-500">
            Organization cannot be changed in ClickHouse database once set
          </p>
        )}
      </div>
      
      <div>
        <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
          Role *
        </label>
        <select
          id="role"
          name="role"
          value={userData.role}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/50 focus:border-primary"
          required
        >
          <option value="user">User</option>
          <option value="admin">Administrator</option>
        </select>
      </div>
      
      {isEditMode && (
        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_active"
            name="is_active"
            checked={userData.is_active}
            onChange={handleChange}
            className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
          />
          <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
            Active
          </label>
        </div>
      )}
      
      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-primary text-secondary rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          disabled={loading}
        >
          {loading ? 'Saving...' : isEditMode ? 'Update User' : 'Create User'}
        </button>
      </div>
    </form>
  );
};

export default UserForm;